// Turns per-clip caption segments (produced live by useCaptionRecognition,
// or edited/added by hand in ClipEditor) into a single WebVTT track for the
// final exported video — and back again, so reopening an already-published
// recording for editing (see ScreenRecordingStudio's `initialClipUrl`) can
// seed its captions from what was published before, not lose them.

const pad2 = (n) => String(Math.floor(n)).padStart(2, '0')
const pad3 = (n) => String(Math.round(n)).padStart(3, '0')

const formatVttTime = (totalSeconds) => {
  const s = Math.max(0, totalSeconds || 0)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = Math.floor(s % 60)
  const ms = Math.round((s - Math.floor(s)) * 1000)
  return `${pad2(h)}:${pad2(m)}:${pad2(sec)}.${pad3(ms)}`
}

/**
 * Flattens every clip's caption segments onto the final, concatenated
 * export timeline, in the same order ffmpegClient's exportClips uses:
 * segments outside a clip's [inSec, outSec) trim window are dropped,
 * segments straddling a trim edge are clipped to it, and every kept segment
 * is shifted by the clip's own trim offset plus the cumulative trimmed
 * duration of every earlier clip.
 */
export const buildCaptionCues = (clips) => {
  const cues = []
  let offset = 0
  for (const clip of clips || []) {
    const inSec = clip.inSec ?? 0
    const outSec = clip.outSec ?? clip.durationSec ?? 0
    const trimmedLen = Math.max(0, outSec - inSec)
    for (const seg of clip.captions || []) {
      const start = Math.max(seg.start, inSec)
      const end = Math.min(seg.end, outSec)
      const text = (seg.text || '').trim()
      if (text && end > start) {
        cues.push({ start: offset + (start - inSec), end: offset + (end - inSec), text })
      }
    }
    offset += trimmedLen
  }
  return cues.sort((a, b) => a.start - b.start)
}

/** Builds a complete WebVTT document from a studio's ordered clips. Returns '' if there's nothing to caption (no <track> should be rendered in that case). */
export const buildVttFromClips = (clips) => {
  const cues = buildCaptionCues(clips)
  if (!cues.length) return ''
  const body = cues
    .map((c, i) => `${i + 1}\n${formatVttTime(c.start)} --> ${formatVttTime(c.end)}\n${c.text}`)
    .join('\n\n')
  return `WEBVTT\n\n${body}\n`
}

const vttTimeToSeconds = (t) => {
  const parts = t.trim().split(':').map(Number)
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  return Number(t) || 0
}

/**
 * Parses a WebVTT string back into `{ id, start, end, text }` segments, so
 * an already-published recording's captions can be loaded into the studio
 * as an editable clip when it's reopened (rather than only being visible as
 * a static track on the plain video preview).
 */
export const parseVttToSegments = (vttText) => {
  if (!vttText || typeof vttText !== 'string') return []
  const lines = vttText.replace(/\r/g, '').split('\n')
  const segments = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (!line.includes('-->')) continue
    const [startStr, endStr] = line.split('-->').map((s) => s.trim())
    const start = vttTimeToSeconds(startStr)
    const end = vttTimeToSeconds((endStr || '').split(/\s+/)[0])
    const textLines = []
    let j = i + 1
    while (j < lines.length && lines[j].trim() !== '') {
      textLines.push(lines[j].trim())
      j++
    }
    const text = textLines.join(' ').trim()
    if (text && end > start) segments.push({ id: `cap_seed_${segments.length}`, start, end, text })
    i = j
  }
  return segments
}
